/**
 * @param func
 * The function to execute after the debounce time
 * @param waitMilliseconds
 * The amount of time the debounce function to wait after the last received action before executing func.
 * @param runOnTimerLeadingEdge
 * This determines if the function should be called on the leading edge and not the trailing.
 * If this is true the function will be called once immediately and then sit idle until the wait period has elapsed after an action.
 * After the idle time has elapsed, the next event will trigger the function and restart the debounce.
 */
export function debounce<FunctionType extends Function>(
  func: FunctionType,
  waitMilliseconds: number,
  runOnTimerLeadingEdge: boolean = false
): FunctionType {
  let timeoutId: NodeJS.Timer | undefined;

  return <FunctionType>(<any>function (this: any, ...args: any[]) {
    const context = <any>this;

    const doLater = function () {
      timeoutId = undefined;
      if (!runOnTimerLeadingEdge) {
        func.apply(context, args);
      }
    };

    const shouldCallNow = runOnTimerLeadingEdge && timeoutId === undefined;
    if (shouldCallNow) {
      func.apply(context, args);
    }

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(doLater, waitMilliseconds);
  });
}
